// components/forms/LoginForm.tsx
"use client";

import { Link } from "@/i18n/routing";
import { useState } from "react";
import { AnimatedLogo } from "./form-logo";
import { useIsMobile, useIsDesktop } from "../../hooks/useBreakpoint";

export function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  
  const handleLogin = async () => {
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/auth/authorize');
      const data = await response.json();
      
      if (data.auth_url) {
        sessionStorage.setItem('oauth_state', data.state);
        window.location.href = data.auth_url;
      }
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };
  
  if (isMobile) {
    return (
      <div className="min-h-screen p-4 flex flex-col items-center justify-center">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo sekce pro mobile */}
          <div className="text-center">
            <AnimatedLogo size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-txt-base)' }}>
              Přihlášení
            </h1>
            <p className="text-sm opacity-75" style={{ color: 'var(--color-txt-base)' }}>
              Vstupte do svého účtu
            </p>
          </div>
          
          {/* Přihlašovací tlačítko */}
          <div className="space-y-4">
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full py-4 px-6 rounded-xl font-medium text-white transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              style={{
                backgroundColor: 'var(--color-accent)',
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Načítání...
                </span>
              ) : (
                'Přihlásit se přes Keycloak'
              )}
            </button>
            
            <div className="text-center text-sm" style={{ color: 'var(--color-txt-base)' }}>
              Nemáte účet?{" "}
              <Link 
                href="/register" 
                className="font-medium hover:underline transition-colors"
                style={{ color: 'var(--color-accent)' }}
              >
                Zaregistrujte se
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Levá strana - Dekorativní panel pro desktop */}
      <div 
        className="hidden lg:flex lg:flex-1 relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-foreground)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br opacity-20"
             style={{ 
               backgroundImage: `linear-gradient(135deg, var(--color-accent), var(--color-secondary))` 
             }}
        />
        
        <div className="relative z-10 w-full h-full flex flex-col justify-center items-center text-center px-12">
          <AnimatedLogo size="lg" className="mb-8" />
          <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--color-txt-base)' }}>
            Vítejte zpět!
          </h2>
          <p className="text-lg opacity-80 max-w-md" style={{ color: 'var(--color-txt-base)' }}>
            Přihlaste se ke svému účtu a pokračujte tam, kde jste skončili.
          </p>
        </div>
        
        {/* Dekorativní elementy */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 transform translate-x-32 -translate-y-32"
             style={{ backgroundColor: 'var(--color-accent)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10 transform -translate-x-24 translate-y-24"
             style={{ backgroundColor: 'var(--color-accent)' }} />
      </div>
      
      {/* Pravá strana - Přihlašovací formulář */}
      <div 
        className="flex-1 lg:max-w-md xl:max-w-lg flex items-center justify-center p-8"
        style={{ backgroundColor: 'var(--color-background)' }}
      >
        <div className="w-full max-w-md space-y-8">
          {/* Logo a nadpis pro menší obrazovky */}
          <div className="text-center lg:hidden">
            <AnimatedLogo size="md" className="mx-auto mb-6" />
          </div>
          
          <div className="space-y-2 text-center lg:text-left">
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-txt-base)' }}>
              Přihlášení
            </h1>
            <p className="opacity-75" style={{ color: 'var(--color-txt-base)' }}>
              Vstupte do svého účtu pomocí Keycloak
            </p>
          </div>
          
          <div className="space-y-6">
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full py-4 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                backgroundColor: 'var(--color-accent)',
                boxShadow: isDesktop ? '0 4px 14px 0 rgba(0, 175, 162, 0.25)' : 'none'
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Načítání...
                </span>
              ) : (
                'Přihlásit se přes Keycloak'
              )}
            </button>
            
            <div className="text-center text-sm" style={{ color: 'var(--color-txt-base)' }}>
              Nemáte účet?{" "}
              <Link 
                href="/register" 
                className="font-medium hover:underline transition-colors"
                style={{ color: 'var(--color-accent)' }}
              >
                Zaregistrujte se
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}